import { useState, useCallback } from "react";
import style from "../style.module.css";
import { formatFileSize } from "lib/files/fileUtils";
import { formatDateString } from "lib/temporal/dateUtils";
import { isVideo } from "lib/isVideo";
import { isGif } from "lib/isGif";
import { getImageSize } from "lib/getImageDetails";
import EllipsisIcon from "views/components/icons/EllipsisIcon";
import Checkbox from "views/components/checkbox";
import SmartImageLabel from "views/components/SmartImage/SmartImageLabel";
import MobileUploadActionSheet from "views/containers/DesignsPageContainer/Workspace/mobile/MobileUploadActionSheet";
import PopoutItemMenu from "views/components/popout/PopoutItemMenu/PopoutItemMenu";
import TrashIcon from "views/components/icons/TrashIcon";
import FolderIcon from "views/components/icons/FolderIcon";
import CheckeredBackgroundIcon from "views/components/icons/CheckeredBackgroundIcon";
import SmartImageIcon from "views/components/icons/SmartImageIcon";
import EditSmartImageIcon from "views/components/icons/EditSmartImageIcon";
import UnassignSmartImageIcon from "views/components/icons/UnassignSmartImageIcon";
import SmartImageModalContainer from "views/containers/SmartImageModalContainer";
import confirmDeleteSmartImage from "views/components/SmartImage/confirmDeleteSmartImage";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface MobileImageListRowProps {
  image: {
    mediaId: string;
    url: string;
    thumbnailUrl: string;
    previewUrl?: string;
    createdAt: string;
    media: {
      name: string;
      asset?: string;
    };
    isPlaceholder?: boolean;
  };
  isLast: boolean;
  isMultiSelect: boolean;
  isSelected: boolean;
  onMultiSelect: () => void;
  onDeleteUserTeamImage: (args: { mediaId: string }) => void;
  onDeleteUserTeamSmartImage?: (args: { id: string; mediaId: string }) => void;
  smartImage?: { id: string; mediaId: string; label: string } | null;
  disableFolderFunctions: boolean;
  folderId?: string | null;
}

const MobileImageListRow = ({
  image,
  isMultiSelect,
  isSelected,
  onMultiSelect,
  onDeleteUserTeamImage,
  onDeleteUserTeamSmartImage,
  smartImage,
  disableFolderFunctions,
  folderId
}: MobileImageListRowProps) => {
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [isSmartImageModalOpen, setIsSmartImageModalOpen] = useState(false);
  const [smartModalType, setSmartModalType] = useState<string>("create");

  const imageSource = image.thumbnailUrl || image.url;
  const imageSize = formatFileSize(getImageSize(image), 1) as string;
  const updatedDate = formatDateString(
    image.createdAt,
    "MMM DD, YYYY"
  ) as string;
  const isVideoFile =
    (imageSource && imageSource.endsWith(".mp4")) ||
    image.media.asset?.toLowerCase().endsWith(".mp4");

  const handleOpenActionSheet = useCallback(() => {
    if (!image.isPlaceholder) {
      setIsActionSheetOpen(true);
    }
  }, [image.isPlaceholder]);

  const handleCloseActionSheet = useCallback(() => {
    setIsActionSheetOpen(false);
  }, []);

  const getActions = useCallback(() => {
    const folderOptionLabel = folderId
      ? "Remove from folder"
      : "Move to folder";

    const actions: any[] = [];

    if (!disableFolderFunctions) {
      actions.push({
        name: folderOptionLabel,
        Icon: FolderIcon as any,
        onClick: () => {
          handleCloseActionSheet();
        }
      });
    }

    if (!isGif(image.url) && !isVideo(image.url)) {
      actions.push({
        name: "AI Background Erase",
        Icon: CheckeredBackgroundIcon as any,
        onClick: () => {
          handleCloseActionSheet();
        }
      });

      if (!smartImage) {
        actions.push({
          name: "Assign Smart ID",
          Icon: SmartImageIcon as any,
          onClick: () => {
            handleCloseActionSheet();
            setSmartModalType("create");
            setIsSmartImageModalOpen(true);
          }
        });
      } else {
        actions.push({
          name: "Edit Smart ID",
          Icon: EditSmartImageIcon as any,
          onClick: () => {
            handleCloseActionSheet();
            setSmartModalType("update");
            setIsSmartImageModalOpen(true);
          }
        });
        actions.push({
          name: "Unassign Smart ID",
          Icon: UnassignSmartImageIcon as any,
          onClick: () => {
            handleCloseActionSheet();
            if (onDeleteUserTeamSmartImage && smartImage) {
              confirmDeleteSmartImage(() => {
                onDeleteUserTeamSmartImage({
                  id: smartImage.id,
                  mediaId: smartImage.mediaId
                });
              });
            }
          }
        });
      }
    }

    actions.push({
      name: "Delete",
      Icon: ((props: any) => <TrashIcon version="2" {...props} />) as any,
      onClick: () => {
        handleCloseActionSheet();
        onDeleteUserTeamImage({ mediaId: image.mediaId });
      }
    });

    return actions;
  }, [
    folderId,
    disableFolderFunctions,
    image,
    smartImage,
    handleCloseActionSheet,
    onDeleteUserTeamImage,
    onDeleteUserTeamSmartImage
  ]);

  return (
    <>
      <div className={style.mobileImageRow}>
        {isMultiSelect && (
          <div className={style.mobileCheckbox}>
            {/* @ts-expect-error JS component has loosely typed props */}
            <Checkbox
              onClick={onMultiSelect}
              checked={isSelected}
              isDoubleScale={true}
            />
          </div>
        )}
        <div className={style.mobileImageTile}>
          {isVideoFile ? (
            <video muted loop={false} autoPlay={false}>
              <source src={imageSource} type="video/mp4" />
            </video>
          ) : (
            <img src={imageSource} alt={image.media.name} />
          )}
        </div>
        <div className={style.mobileImageInfo}>
          <div className={style.mobileImageName}>
            {smartImage && <SmartImageLabel label={smartImage.label} />}
            {image.media.name}
          </div>
          <div className={style.mobileImageMeta}>
            {imageSize} &middot; {updatedDate}
          </div>
        </div>
        <button
          className={style.mobileActionButton}
          onClick={handleOpenActionSheet}
          type="button"
          aria-label="Image actions"
        >
          {/* @ts-expect-error JS icon component lacks proper TS types */}
          <EllipsisIcon color="#999c9e" size="24px" />
        </button>
      </div>
      <MobileUploadActionSheet
        isOpen={isActionSheetOpen}
        onClose={handleCloseActionSheet}
      >
        {/* @ts-expect-error JS component has loosely typed props */}
        <PopoutItemMenu
          actions={getActions()}
          onClose={handleCloseActionSheet}
          itemName={image.media.name}
        />
      </MobileUploadActionSheet>
      {isSmartImageModalOpen && (
        <SmartImageModalContainer
          isOpen={isSmartImageModalOpen}
          onClose={() => setIsSmartImageModalOpen(false)}
          image={image}
          smartImage={smartImage}
          type={smartModalType}
          onTransferClick={() => setIsSmartImageModalOpen(false)}
        />
      )}
    </>
  );
};

export default MobileImageListRow;
